import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { KOMMUN_LINKS, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN, LAST_REVIEWED } from "@/lib/official-sources";

const faqs = [
  { q: "Vart ansöker jag om serveringstillstånd i Linköping?", a: "Ansökan görs till Bygg- och miljönämnden / tillståndsenheten i Linköpings kommun. Verksamhet i annan kommun i Östergötlands län (t.ex. Mjölby, Åtvidaberg, Söderköping, Norrköping) söker hos respektive kommun." },
  { q: "Vad kostar serveringstillstånd i Linköping?", a: "Linköping kommun tar ut en ansökningsavgift och en årlig tillsynsavgift. Avgifterna varierar beroende på tillståndstyp och alkoholförsäljningens storlek – kontrollera aktuella avgifter på kommunens webbplats." },
  { q: "Hur lång är handläggningstiden i Linköping?", a: "Linköping kommun anger normalt 8–12 veckor för en komplett ansökan om stadigvarande tillstånd. Tillfälliga tillstånd kan gå snabbare." },
];

export const Route = createFileRoute("/serveringstillstand-linkoping")({
  component: () => (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Guider", to: "/serveringstillstand-och-kunskapsprov" },
        { label: "Linköping" },
      ]}
      title="Serveringstillstånd Linköping – ansökan och krav"
      intro="Allt du behöver veta om serveringstillstånd i Linköping: var du ansöker, vilka krav som ställs och hur du förbereder dig för kunskapsprovet."
      lastReviewed={LAST_REVIEWED}
      sources={[KOMMUN_LINKS.linkoping, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN]}
      localData={{
        unit: "Linköpings kommun – Bygg- och miljönämnden, tillståndsenheten",
        address: "Miljökontoret, Linköpings kommun, 581 81 Linköping",
        phone: "013-20 60 00 (kommunens växel/kontaktcenter)",
        waitTime: "ca 8–12 veckor för stadigvarande tillstånd från komplett ansökan",
        feeExamples: [
          "Nyansökan/ägarskifte: ca 11 000 kr (enligt kommunens taxa)",
          "Stadigvarande ändring i tillstånd: ca 4 395 kr",
          "Tillfälligt tillstånd till allmänheten: ca 4 395 kr, plus ca 1 105 kr per ytterligare dag",
        ],
        checklist: [
          "Registreringsbevis",
          "Finansieringsplan och kontoutdrag",
          "Hyresavtal eller nyttjanderätt till lokalen",
          "Ritning över serveringsyta",
          "Brandskyddsdokumentation",
          "PBI-uppgifter och godkänt kunskapsprov",
        ],
        officialUrl: KOMMUN_LINKS.linkoping,
      }}
      sections={[
        { h2: "Var ansöker du i Linköping?", body: "Bygg- och miljönämnden / tillståndsenheten i Linköpings kommun ansvarar för serveringstillstånd i Linköping kommun. För verksamhet i kringliggande kommuner som Mjölby, Åtvidaberg, Söderköping, Norrköping ansöker du hos respektive kommun. Varje kommun har egna blanketter och e-tjänster, men kraven enligt alkohollagen är nationella och gäller överallt." },
        { h2: "Krav från Linköping kommun", body: "Utöver de nationella kraven (personlig och ekonomisk lämplighet, godkänt kunskapsprov och lämplig lokal) kan kommunen ha lokala riktlinjer kring serveringstider, uteservering och ordning. Från 1 juni 2026 är kraven på eget kök och varierat matutbud borttagna nationellt. Linköping har ett växande nöjesliv kring centrum och universitetsområdet med fokus på ordning och nykterhet vid större evenemang." },
        { h2: "Kunskapsprovet i Linköping", body: "Provet genomförs hos kommunen efter att ansökan har kommit in. Det är ett skriftligt prov inom alkohollagen, serveringsbestämmelser, ordning och nykterhet, marknadsföring m.m. Frågeantalet varierar beroende på tillståndstyp och kommun – Folkhälsomyndighetens föreskrifter FoHMFS 2014:7 anger områden och miniminivåer. Med Alkoholprovet kan du träna i samma format hemifrån och gå in på provet förberedd." },
        { h2: "Avgifter och handläggningstid i Linköping", body: "Linköpings kommun tar ut en ansökningsavgift enligt fastställd taxa – för en nyansökan eller vid ägarskifte handlar det om ca 11 000 kr, och tillfälligt tillstånd till allmänheten kostar ca 4 395 kr plus en avgift per extra dag. Kontrollera alltid aktuella belopp på kommunens sida eftersom taxor revideras. Räkna med 8–12 veckor från komplett ansökan till beslut om stadigvarande tillstånd; ofullständig ansökan eller komplicerad lämplighetsprövning förlänger tiden." },
        { h2: "Tillsyn och uppföljning", body: "Linköping kommun bedriver aktiv tillsyn på serveringsställen tillsammans med polis och Skatteverket. Anmärkningar kan leda till erinran, varning, sanktionsavgift eller återkallat tillstånd. Goda kunskaper i alkohollagen minskar risken för misstag i den dagliga driften." },
      ]}
      faqs={faqs}
      related={{
        title: "Relaterade sidor",
        links: [
          { to: "/serveringstillstand-och-kunskapsprov", label: "Hub – alla guider" },
          { to: "/serveringstillstand-stockholm", label: "Serveringstillstånd Stockholm" },
          { to: "/serveringstillstand-goteborg", label: "Serveringstillstånd Göteborg" },
          { to: "/serveringstillstand-malmo", label: "Serveringstillstånd Malmö" },
          { to: "/serveringstillstand-restaurang", label: "För restaurang" },
          { to: "/serveringstillstand-bar", label: "För bar & nattklubb" },
          { to: "/tillfalligt-serveringstillstand", label: "Tillfälligt tillstånd" },
          { to: "/kunskapsprov-alkohollagen", label: "Kunskapsprov i alkohollagen" },
          { to: "/foretag", label: "Företagslösning" },
        ],
      }}
    />
  ),
  head: () => ({
    meta: [
      { title: "Serveringstillstånd Linköping – krav, prov & tips" },
      { name: "description", content: "Så får du serveringstillstånd i Linköping: lokala krav, tillsynsavgifter och hur du klarar kunskapsprovet." },
      { property: "og:title", content: "Serveringstillstånd Linköping – tips" },
      { property: "og:description", content: "Ansökan, krav, avgifter och kunskapsprov för serveringstillstånd i Linköping." },
      { property: "og:url", content: "https://alkoholprovet.se/serveringstillstand-linkoping" },
    ],
    links: [{ rel: "canonical", href: "https://alkoholprovet.se/serveringstillstand-linkoping" }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Guider", url: "https://alkoholprovet.se/serveringstillstand-och-kunskapsprov" },
        { label: "Linköping", url: "https://alkoholprovet.se/serveringstillstand-linkoping" },
      ]),
    ],
  }),
});
